import React, {
  createRef,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'
import cn from 'classnames'
import { motion, useDragControls, Variants } from 'framer-motion'
import Dialog from 'core/components/dialogs/Dialog'
import Tooltip from 'core/components/dialogs/Tooltip'
import Float from 'core/components/dialogs/Float'
import useLocalStorage from 'core/hooks/useLocalStorage'
import debounce from 'lodash.debounce'
import Icon from 'core/components/icons/Icon'
import CgArrowsCompressLeft from 'core/_tmp/react-icons/CgArrowsCompressLeft'
import CgArrowsExpandLeft from 'core/_tmp/react-icons/CgArrowsExpandLeft'

export interface DrawerApi {
  expandDrawer: VoidFunction
  compressDrawer: VoidFunction
}

interface Props {
  id?: string
  show: boolean
  width?: 'xs' | 'sm' | 'md' | 'lg' | string
  startFrom?: 'left' | 'right'
  onClose?: VoidFunction
  enableBackdrop?: boolean
  hideIcons?: boolean
  contentClassNames?: string
  children?: ReactNode
  title?: ReactNode
  canExpand?: boolean
  canResize?: boolean
  leftPaddingDragLimit?: string
  rightPaddingDragLimit?: string
  setDrawerApi?: (api: DrawerApi) => void
  onExpandChange?: (isExpanded: boolean) => void
}

const SizeConfig: Record<string, { content: string }> = {
  xs: {
    content: 'w-full md:w-1/6'
  },
  sm: {
    content: 'w-full md:w-2/6'
  },
  md: {
    content: 'w-full md:w-4/6 lg:w-3/6'
  },
  lg: {
    content: 'w-full md:w-5/6 lg:w-4/6'
  },
  xl: {
    content: 'w-full md:w-5/6 lg:w-3/4'
  }
}

const IconWrapper = ({
  className,
  children,
  tooltipLabel,
  onClick
}: {
  className?: string
  tooltipLabel?: string
  children?: React.ReactElement
  onClick?: VoidFunction
}) => {
  return (
    <Tooltip
      content={
        <div className="card bg-dark-0/70 p-2 text-white">{tooltipLabel}</div>
      }
      useArrow={false}
      triggerMode="hover"
      position="bottom-middle"
    >
      <span
        onClick={onClick}
        className={cn([
          'cursor-pointer hover:bg-dark-1 rounded p-1.5',
          className
        ])}
      >
        {children}
      </span>
    </Tooltip>
  )
}

const Drawer = ({
  id,
  show,
  width = 'sm',
  onClose,
  enableBackdrop,
  hideIcons,
  contentClassNames = 'pb-20 px-6 pt-4 overflow-scroll h-screen',
  startFrom = 'left',
  children,
  title,
  canExpand = true,
  canResize = false,
  leftPaddingDragLimit = 'md:pl-64',
  rightPaddingDragLimit = 'md:pr-56',
  setDrawerApi,
  onExpandChange
}: Props) => {
  const localStorageDrawerName = useMemo(
    () => (id ? `drawer_width_${id}` : 'drawer_width_general'),
    [id]
  )
  const [storedDrawerWidth, setStoredDrawerWidth] = useLocalStorage(
    localStorageDrawerName,
    ''
  )
  const dragBoundingBoxRef = createRef<HTMLDivElement>()
  const dragabbleBarRef = createRef<HTMLDivElement>()
  const draggableBarTooltipRef = createRef<HTMLSpanElement>()
  const [draggableBarLeft, setDraggableBarLeft] = useState('0')
  const contentRef = createRef<HTMLDivElement>()
  const [isExpanded, setExpanded] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const dragControls = useDragControls()

  const { content } = SizeConfig[width || 'sm'] || {}

  const closeDrawer = useCallback(() => {
    if (isDragging) return
    onClose?.()
    setDraggableBarLeft('0')
  }, [isDragging, onClose])

  const variants: Variants = useMemo(
    () => ({
      open: {
        opacity: 1,
        x: 0,
        transition: { duration: 0.2 }
      },
      closed: {
        opacity: 0,
        transition: { duration: 0.2 },
        x: startFrom === 'left' ? '-100%' : '100%'
      }
    }),
    [startFrom]
  )

  const icons = useMemo(() => {
    return (
      <div className={cn('flex gap-4', hideIcons && 'md:hidden')}>
        <IconWrapper onClick={closeDrawer} tooltipLabel="Close">
          <Icon
            name="close"
            noWrapper
            size={17}
            className={cn([
              'text-dark-4 text-lg',
              startFrom === 'left' && 'rotate-180'
            ])}
          />
        </IconWrapper>
        {canExpand && (
          <IconWrapper
            tooltipLabel={isExpanded ? 'Compress' : 'Expand'}
            onClick={() => {
              setExpanded(!isExpanded)
              onExpandChange?.(!isExpanded)
            }}
            className="xs:hidden md:inline"
          >
            {isExpanded ? (
              <CgArrowsCompressLeft className="text-dark-4 text-lg" />
            ) : (
              <CgArrowsExpandLeft className="text-dark-4 text-lg" />
            )}
          </IconWrapper>
        )}
      </div>
    )
  }, [canExpand, closeDrawer, hideIcons, isExpanded, onExpandChange, startFrom])

  const contentStyle = useMemo(
    () =>
      !isExpanded && canResize && storedDrawerWidth
        ? { width: storedDrawerWidth }
        : {},
    [canResize, isExpanded, storedDrawerWidth]
  )

  // Draggable Bar memos and callbacks
  const draggableBarStyle = useMemo(
    () => ({
      touchAction: 'none',
      left: draggableBarLeft
    }),
    [draggableBarLeft]
  )

  const onHoverStart = useCallback(() => {
    if (contentRef.current && draggableBarTooltipRef.current) {
      draggableBarTooltipRef.current.style.opacity = '1'
    }
  }, [contentRef, draggableBarTooltipRef])

  const onHoverEnd = useCallback(() => {
    if (draggableBarTooltipRef.current) {
      draggableBarTooltipRef.current.style.opacity = ''
    }
  }, [draggableBarTooltipRef])

  const onDragStart = useCallback(() => {
    setIsDragging(true)
    onHoverEnd()
  }, [onHoverEnd])

  const onDrag = useCallback(() => {
    if (contentRef.current) {
      const dragabbleBar =
        dragabbleBarRef.current ?? document.querySelector('#dragabble-bar')
      const translateX = parseFloat(
        (dragabbleBar?.getAttribute('style')?.split(' ') ?? [])
          .find((item) => item.startsWith('translateX('))
          ?.replace('translateX(', '')
          ?.replace('px)', '') ?? '0'
      )
      const barPosition = (dragabbleBar?.offsetLeft ?? 0) + translateX
      contentRef.current.style.width =
        window.innerWidth - barPosition - 7 + 'px'
    }
  }, [contentRef, dragabbleBarRef])

  const onDraggableBarClicked = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (isDragging) {
        e.stopPropagation()
        return
      }
      onClose?.()
      setDraggableBarLeft('0')
    },
    [isDragging, onClose]
  )

  const onDragEnd = useCallback(() => {
    // This timeout it's necessary to avoid the drawer to close after the user released the mouse button, because it will cause a click event. Checkout onDraggableBarClicked()
    setTimeout(() => {
      setIsDragging(false)
      if (contentRef.current?.style?.width) {
        setStoredDrawerWidth(contentRef.current.style.width)
      }
    }, 300)
  }, [contentRef, setStoredDrawerWidth])

  useEffect(() => {
    if (!show) {
      setExpanded(false)
      onExpandChange?.(false)
    }
  }, [onExpandChange, show])

  useEffect(() => {
    setDrawerApi?.({
      expandDrawer: () => {
        setExpanded(true)
      },
      compressDrawer: () => {
        setExpanded(false)
      }
    })
  }, [setDrawerApi])

  useEffect(() => {
    if (
      contentRef.current &&
      dragabbleBarRef.current &&
      draggableBarLeft === '0'
    ) {
      const left =
        contentRef.current.offsetLeft - dragabbleBarRef.current.clientWidth / 2
      setDraggableBarLeft(`${left}px`)
    }
  }, [contentRef, dragabbleBarRef, draggableBarLeft])

  useEffect(() => {
    const onMouseMove = ({ clientY }: MouseEvent) => {
      if (
        show &&
        !isExpanded &&
        canResize &&
        contentRef.current &&
        draggableBarTooltipRef.current
      ) {
        const { clientHeight } = draggableBarTooltipRef.current
        const bottomLimit = window.innerHeight - clientHeight
        const halfTooltip = clientHeight / 2
        let newTop = clientY - halfTooltip
        newTop =
          newTop < halfTooltip ? 0 : newTop > bottomLimit ? bottomLimit : newTop
        draggableBarTooltipRef.current.style.top = `${newTop}px`
        const spaceToLeftEdgeMinusTooltipWidth =
          window.innerWidth -
          contentRef.current.clientWidth -
          draggableBarTooltipRef.current.clientWidth
        if (spaceToLeftEdgeMinusTooltipWidth < 20) {
          draggableBarTooltipRef.current.style.right = `${
            contentRef.current.clientWidth -
            draggableBarTooltipRef.current.clientWidth -
            5
          }px`
        } else {
          draggableBarTooltipRef.current.style.right = `${
            contentRef.current.clientWidth + 5
          }px`
        }
      }
    }
    document.addEventListener('mousemove', onMouseMove)
    return () => document.removeEventListener('mousemove', onMouseMove)
  }, [
    show,
    isExpanded,
    canResize,
    contentRef,
    draggableBarTooltipRef,
    localStorageDrawerName
  ])

  useEffect(() => {
    const handleResize = debounce(() => {
      if (canResize) {
        onDrag()
      }
    }, 500)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [canResize, onDrag])

  useEffect(() => {
    const listener = (e: KeyboardEvent) => {
      // Don't close drawer if a modal is showing on top
      const hasModalOpen = !!document.getElementById('core-modal')

      if (e.key === 'Escape' && show && !hasModalOpen) {
        onClose?.()
      }
    }
    document.addEventListener('keyup', listener)
    return () => document.removeEventListener('keyup', listener)
  }, [onClose, show])

  return (
    <>
      <Dialog
        className={cn([
          'top-0  w-full',
          startFrom === 'right' ? 'right-0' : 'left-0'
        ])}
        show={show}
        motionProps={variants}
        hasBackdrop={enableBackdrop}
      >
        {/* Draggable area */}
        {canResize && (
          <div
            className={cn(
              'absolute left-0 top-0 opacity-40 h-screen w-full',
              leftPaddingDragLimit,
              rightPaddingDragLimit
            )}
            onClick={closeDrawer}
          >
            <div ref={dragBoundingBoxRef} className="h-screen opacity-40"></div>
          </div>
        )}
        <div className={cn('w-full h-screen relative')} onClick={closeDrawer}>
          {canResize && (
            <motion.div
              ref={dragabbleBarRef}
              id="dragabble-bar"
              className="absolute left-0 top-0 z-20 w-4 -translate-x-1/2 h-full cursor-col-resize"
              drag={isExpanded ? false : 'x'}
              dragControls={dragControls}
              dragConstraints={dragBoundingBoxRef}
              dragElastic={0}
              dragMomentum={false}
              onDragStart={onDragStart}
              onDrag={onDrag}
              onClick={onDraggableBarClicked}
              onClickCapture={onHoverEnd}
              onDragEnd={onDragEnd}
              style={draggableBarStyle}
              onHoverStart={onHoverStart}
              onHoverEnd={onHoverEnd}
            >
              <Float>
                <span
                  ref={draggableBarTooltipRef}
                  className={cn(
                    'transition-opacity duration-500 opacity-0 absolute card w-32 bg-dark-0/90 p-1 text-white text-center text-sm font-bold',
                    isExpanded ? 'z-0' : 'z-[48]'
                  )}
                >
                  Drag to resize
                  <br />
                  Click to close
                </span>
              </Float>
            </motion.div>
          )}
          <div
            ref={contentRef}
            className={cn([
              'bg-white shadow-md absolute top-0 rounded-md max-w-full',
              startFrom === 'left' ? 'left-0' : 'right-0',
              !isDragging && 'transition-all',
              isExpanded && '!w-full',
              content ?? width
            ])}
            onClick={(e) => e.stopPropagation()}
            style={contentStyle}
          >
            <div
              className={cn([
                'w-full px-6 pt-6 pb-4 border-b border-dark-8 flex gap-5 items-center',
                startFrom === 'left' && 'justify-between'
              ])}
            >
              {startFrom === 'right' && icons}
              {title ?? <span />}
              {startFrom === 'left' && icons}
            </div>
            <div className={contentClassNames} id="drawer-content">
              {children}
            </div>
          </div>
        </div>
      </Dialog>
    </>
  )
}

export default Drawer
